import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { CustomImage } from '../lib/tiptapExtensions';
import Highlight from '@tiptap/extension-highlight';
import { EmailButton } from './EmailButton';
import { BlogTeaserBox } from './BlogTeaserBox';
import { InsertButtonModal } from './InsertButtonModal';
import { EditBlogTeaserModal } from './EditBlogTeaserModal';
import { ButtonActionBar } from './ButtonActionBar';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { PreserveStyleLink } from './PreserveStyleLink';
import InsertImageModal from './InsertImageModal';
import { HTMLViewModal } from './HTMLViewModal';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Heading1,
  Heading2,
  User,
  Highlighter,
  Undo,
  Redo,
  RectangleHorizontal,
  Code,
  Smartphone,
  Monitor,
} from 'lucide-react';

interface CampaignEditorProps {
  content: string;
  onChange: (html: string) => void;
  onInsertFirstName?: () => void;
  onSaveNow?: () => void;
  /**
   * Tailwind top-offset voor de sticky toolbar. Default schuift hij aan
   * onder de FocusHeader (h-16) van de user-schermen; schermen zonder
   * sticky header (admin) geven 'top-0' mee.
   */
  toolbarStickyClass?: string;
}

export function CampaignEditor({ content, onChange, onInsertFirstName, onSaveNow, toolbarStickyClass = 'top-16' }: CampaignEditorProps) {
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingButton, setEditingButton] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  const [showTeaserModal, setShowTeaserModal] = useState(false);
  const [editingTeaser, setEditingTeaser] = useState<any>(null);
  // Emails worden vooral op mobiel gelezen; de editor toont daarom standaard
  // de smalle mobiele breedte, met een toggle naar de brede desktopweergave.
  const [viewMode, setViewMode] = useState<'mobile' | 'desktop'>('mobile');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        link: false,
      }),
      Underline,
      EmailButton,
      BlogTeaserBox,
      PreserveStyleLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          style: 'color: #2563eb; text-decoration: underline; font-weight: normal;',
        },
      }),
      CustomImage.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
      Highlight.configure({
        multicolor: false,
      }),
    ],
    content,
    editorProps: {
      attributes: {
        class: 'email-content focus:outline-none min-h-[400px] p-4',
        style: 'font-family: Arial, Helvetica, sans-serif; font-size: 16px; line-height: 1.6; color: #374151;',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onBlur: () => {
      if (onSaveNow) {
        onSaveNow();
      }
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;

    const handleInlineEdit = () => {
      handleEditButton();
    };

    const handleInlineDelete = () => {
      handleDeleteButtonClick();
    };

    const handleTeaserEdit = (e: Event) => {
      const customEvent = e as CustomEvent;
      setEditingTeaser(customEvent.detail);
      setShowTeaserModal(true);
    };

    document.addEventListener('emailButtonEdit', handleInlineEdit);
    document.addEventListener('emailButtonDelete', handleInlineDelete);
    document.addEventListener('blogTeaserBoxEdit', handleTeaserEdit);

    return () => {
      document.removeEventListener('emailButtonEdit', handleInlineEdit);
      document.removeEventListener('emailButtonDelete', handleInlineDelete);
      document.removeEventListener('blogTeaserBoxEdit', handleTeaserEdit);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('Voer URL in:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    setShowImageModal(true);
  };

  const handleInsertImage = (data: { src: string; width: string; height: string; alt: string }) => {
    if (!editor) return;

    const widthNum = parseInt(data.width, 10);
    const heightNum = parseInt(data.height, 10);
    editor.chain().focus().setImage({
      src: data.src,
      alt: data.alt,
      width: Number.isFinite(widthNum) ? widthNum : undefined,
      height: Number.isFinite(heightNum) ? heightNum : undefined,
    }).run();
  };

  const insertFirstName = () => {
    onInsertFirstName?.();
  };

  const handleAddButton = () => {
    setEditingButton(null);
    setShowButtonModal(true);
    setIsModalOpen(true);
  };

  const handleEditButton = () => {
    if (!editor) return;

    const { selection } = editor.state;
    const node = editor.state.doc.nodeAt(selection.from);

    if (node && node.type.name === 'emailButton') {
      setEditingButton({
        href: node.attrs.href,
        text: node.attrs.text,
        variant: node.attrs.variant,
        alignment: node.attrs.alignment,
        size: node.attrs.size,
      });
      setShowButtonModal(true);
      setIsModalOpen(true);
    }
  };

  const handleDeleteButtonClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (!editor) return;

    const { selection } = editor.state;
    const node = editor.state.doc.nodeAt(selection.from);

    if (node && node.type.name === 'emailButton') {
      editor.chain().focus().deleteSelection().run();
    }

    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleInsertButton = (options: {
    href: string;
    text: string;
    variant: string;
    alignment: string;
    size: string;
  }) => {
    if (!editor) return;

    if (editingButton) {
      const { selection } = editor.state;
      const node = editor.state.doc.nodeAt(selection.from);

      if (node && node.type.name === 'emailButton') {
        editor.chain().focus().updateEmailButton(options).run();
      }
    } else {
      editor.chain().focus().setEmailButton(options).run();
    }

    setEditingButton(null);
    setIsModalOpen(false);
  };

  const handleHtmlApply = (html: string) => {
    if (!editor) return;
    editor.commands.setContent(html);
    onChange(html);
  };

  const handleUpdateTeaser = (options: {
    title: string;
    intro: string;
    imageUrl?: string;
    buttonText: string;
    buttonUrl: string;
    accentColor?: string;
  }) => {
    if (!editor) return;

    const { selection } = editor.state;
    const node = editor.state.doc.nodeAt(selection.from);

    if (node && node.type.name === 'blogTeaserBox') {
      editor.chain().focus().updateBlogTeaserBox(options).run();
      onChange(editor.getHTML());
    }

    setEditingTeaser(null);
  };

  return (
    <>
      <InsertButtonModal
        isOpen={showButtonModal}
        onClose={() => {
          setShowButtonModal(false);
          setIsModalOpen(false);
        }}
        onInsert={handleInsertButton}
        initialValues={editingButton}
      />

      <EditBlogTeaserModal
        isOpen={showTeaserModal}
        onClose={() => {
          setShowTeaserModal(false);
          setEditingTeaser(null);
        }}
        onUpdate={handleUpdateTeaser}
        initialValues={editingTeaser}
      />

      {showImageModal && (
        <InsertImageModal
          onInsert={handleInsertImage}
          onClose={() => setShowImageModal(false)}
        />
      )}

      {showHtmlModal && editor && (
        <HTMLViewModal
          html={editor.getHTML()}
          onClose={() => setShowHtmlModal(false)}
          onApply={handleHtmlApply}
          title="Campaign HTML"
        />
      )}

      <ButtonActionBar
        editor={editor}
        onEdit={handleEditButton}
        onDelete={handleDeleteButtonClick}
        isModalOpen={isModalOpen}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Geen overflow-hidden op de container: dat schakelt position:sticky
          van de toolbar uit. Ronde hoeken zitten daarom op toolbar en body. */}
      <div className="border border-gray-300 rounded-lg bg-white relative">
      <div className={`border-b border-gray-300 bg-gray-50 p-2 flex flex-wrap gap-1 rounded-t-lg sticky z-30 ${toolbarStickyClass}`}>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('bold') ? 'bg-gray-300' : ''
          }`}
          title="Vet (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('italic') ? 'bg-gray-300' : ''
          }`}
          title="Cursief (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('underline') ? 'bg-gray-300' : ''
          }`}
          title="Onderstrepen (Ctrl+U)"
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('highlight') ? 'bg-gray-300' : ''
          }`}
          title="Markeren"
        >
          <Highlighter className="w-4 h-4" />
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''
          }`}
          title="Kop 1"
        >
          <Heading1 className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''
          }`}
          title="Kop 2"
        >
          <Heading2 className="w-4 h-4" />
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('bulletList') ? 'bg-gray-300' : ''
          }`}
          title="Opsommingslijst"
        >
          <List className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('orderedList') ? 'bg-gray-300' : ''
          }`}
          title="Genummerde lijst"
        >
          <ListOrdered className="w-4 h-4" />
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        <button
          onClick={addLink}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('link') ? 'bg-gray-300' : ''
          }`}
          title="Link toevoegen"
        >
          <LinkIcon className="w-4 h-4" />
        </button>

        <button
          onClick={addImage}
          className="p-2 rounded hover:bg-gray-200 transition-colors"
          title="Afbeelding toevoegen"
        >
          <ImageIcon className="w-4 h-4" />
        </button>

        <button
          onClick={handleAddButton}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('emailButton') ? 'bg-gray-300' : ''
          }`}
          title="Button toevoegen"
        >
          <RectangleHorizontal className="w-4 h-4" />
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        <button
          onClick={() => setShowHtmlModal(true)}
          className="p-2 rounded hover:bg-gray-200 transition-colors bg-blue-50 hover:bg-blue-100"
          title="HTML Bekijken/Bewerken"
        >
          <Code className="w-4 h-4 text-blue-600" />
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        <button
          onClick={insertFirstName}
          className="p-2 rounded hover:bg-gray-200 transition-colors bg-blue-50 hover:bg-blue-100"
          title="Voeg voornaam placeholder toe"
        >
          <User className="w-4 h-4 text-blue-600" />
        </button>

        <div className="w-px bg-gray-300 mx-1" />

        <button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Ongedaan maken (Ctrl+Z)"
        >
          <Undo className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Opnieuw (Ctrl+Y)"
        >
          <Redo className="w-4 h-4" />
        </button>

        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setViewMode('mobile')}
            className={`p-2 rounded hover:bg-gray-200 transition-colors ${
              viewMode === 'mobile' ? 'bg-gray-300' : ''
            }`}
            title="Mobiele weergave (zo lezen de meeste ontvangers je email)"
          >
            <Smartphone className="w-4 h-4" />
          </button>

          <button
            onClick={() => setViewMode('desktop')}
            className={`p-2 rounded hover:bg-gray-200 transition-colors ${
              viewMode === 'desktop' ? 'bg-gray-300' : ''
            }`}
            title="Brede desktop weergave"
          >
            <Monitor className="w-4 h-4" />
          </button>
        </div>
      </div>

        {viewMode === 'mobile' ? (
          <div className="bg-gray-100 px-4 py-4 rounded-b-lg">
            <div className="max-w-[420px] mx-auto bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <EditorContent editor={editor} />
            </div>
            <p className="text-center text-xs text-gray-400 mt-2">
              Mobiele weergave · gebruik de knoppen rechtsboven voor de brede weergave
            </p>
          </div>
        ) : (
          <div className="rounded-b-lg overflow-hidden">
            <EditorContent editor={editor} />
          </div>
        )}
      </div>
    </>
  );
}
